import { Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import * as cookie from "cookie";
import { checkTokenExpired } from "@utils/common";
import { useAppSelector, useAppDispatch } from "@redux/hooks";
import { useState, useEffect, useMemo } from "react";
import { setAccessToken } from "@redux/slices/AuthSlice";
import {
  EnabledNavigation,
  notifyUser,
  setProfileData,
  setShowAvailableBalance,
} from "@redux/slices/CommonSlice";
import API_URLS, { API_BASE_URL } from "@constants/apiUrls";
import PageLoader from "@features/common/pageLoader/PageLoader";
import { useFetchProfileQuery } from "@features/common/commonApi";
import { PAGE_URLS } from "@constants/common";
import {
  getFirstAccessibleRoute,
  handleRedirectByTab,
} from "@utils/redirectUtils";

// --- Utility Functions for Clarity ---

/** Clears the access token cookie. */
const clearAccessTokenCookie = () => {
  document.cookie =
    "access_token_expiry=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
};

/** Attempts to refresh the access token via the backend. */
const attemptTokenRefresh = async () => {
  try {
    const response = await fetch(API_BASE_URL + API_URLS.REFRESH_TOKEN, {
      method: "POST",
      credentials: "include",
    });

    if (response.status === 400) {
      return { error400: true };
    }

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (!data?.access) {
      return null;
    }
    return { access: data.access };
  } catch (error) {
    return null;
  }
};

const PrivateRoute = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasHandledInitialRedirect, setHasHandledInitialRedirect] =
    useState(false);

  const {
    data: profileResponse,
    isLoading: isProfileLoading,
    refetch: refetchProfile,
  } = useFetchProfileQuery(undefined, {
    skip: !isAuthenticated,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    let isMounted = true; // Prevents state updates on unmounted components

    const verify = async () => {
      setIsChecking(true);

      const cookies = cookie.parse(document.cookie);
      const currentExpiry = cookies.access_token_expiry;

      // 1. If we have a token in Redux AND a valid cookie, we are logged in.
      // We check the token value directly from the Redux selector at this moment.
      if (accessToken && currentExpiry && !checkTokenExpired(currentExpiry)) {
        if (isMounted) {
          setIsAuthenticated(true);
          setIsChecking(false);
        }
        return;
      }

      // 2. If Redux is empty OR cookie is expired, try to refresh
      const result = await attemptTokenRefresh();

      if (isMounted) {
        if (result?.access) {
          dispatch(setAccessToken(result.access));
          setIsAuthenticated(true);
        } else {
          clearAccessTokenCookie();
          setIsAuthenticated(false);

          // Only show notification if it's a specific refresh error (400)
          if (result?.error400) {
            dispatch(
              notifyUser({
                title: "Session expired",
                message: "Your session has expired. Please log in again.",
                severity: "error",
              }),
            );
          }
        }
        setIsChecking(false);
      }
    };

    verify();

    return () => {
      isMounted = false;
    };
    // Re-verify if dispatch or accessToken changes
  }, [dispatch, accessToken]);

  // Refetch profile when route changes
  useEffect(() => {
    if (isAuthenticated && refetchProfile) {
      refetchProfile();
    }
  }, [location.pathname, isAuthenticated, refetchProfile]);

  /**
   * Profile Data Processing & Route Authorization Effect
   *
   * Runs whenever user profile data is fetched or route changes.
   *
   * Responsibilities:
   * 1. Store user profile data in Redux for global access
   * 2. Check if user has permission to access current route (role-based access control)
   * 3. Handle initial navigation after login based on redirect params or user role
   *
   * Flow:
   * - Store profile → Check permissions → Redirect if unauthorized → Handle initial navigation
   */

  useEffect(() => {
    // Only run if profile data has been successfully fetched
    if (profileResponse?.data) {
      // Step 1: Extract user profile data from API response
      const {
        corporate_name,
        email,
        full_name,
        first_name,
        last_name,
        enabled_navigation, // Navigation permissions based on user role
        redirect_tab, // Optional redirect hint from backend
      } = profileResponse.data;

      // Step 2: Store user profile in Redux state for global access
      // This makes user info available throughout the app (header, settings, etc.)
      dispatch(
        setProfileData({
          corporate_name: corporate_name || "",
          email: email || "",
          full_name: full_name || "",
          first_name: first_name || "",
          last_name: last_name || "",
          enabled_navigation: enabled_navigation as EnabledNavigation,
        }),
      );

      const wallet_balance = enabled_navigation?.wallet_balance;

      dispatch(setShowAvailableBalance(wallet_balance || false));

      // Step 3: Build permissions object for easier access
      const permissions = {
        hasReport: enabled_navigation?.reports,
        hasCreditReport: enabled_navigation?.credit_reports,
        hasIssueCredit: enabled_navigation?.credit_upload,
        hasExpiryReminder: enabled_navigation?.expiry_reminders,
        hasDrafts: enabled_navigation?.saved_orders,
        hasUserManagement: enabled_navigation?.user_management,
      };

      // Step 4: Check permissions BEFORE any redirect logic
      // Remove trailing slashes from current path for consistent matching
      const normalizedPath = location.pathname.replace(/\/+$/, "");

      // Map each protected route to its required permission
      // Only routes in this map are permission-protected
      const accessMap: Record<string, boolean | undefined> = {
        [PAGE_URLS.REPORTS]: permissions.hasReport,
        [PAGE_URLS.SEND_REMINDER]: permissions.hasExpiryReminder,
        [PAGE_URLS.USER_MANAGEMENT]: permissions.hasUserManagement,
        [PAGE_URLS.DRAFTS]: permissions.hasDrafts,
        [PAGE_URLS.ISSUE_CREDITS]: permissions.hasIssueCredit,
        [PAGE_URLS.CREDIT_REPORTS]: permissions.hasCreditReport,
        [PAGE_URLS.CREDITS_LISTING]: permissions.hasIssueCredit,
        [PAGE_URLS.ISSUE_CREDITS_SUCCESS]: permissions.hasIssueCredit,
      };

      // Step 5: If user is on a protected route but lacks permission, deny access
      // Only checks routes that exist in accessMap (other routes are unrestricted)
      if (normalizedPath in accessMap && !accessMap[normalizedPath]) {
        navigate(PAGE_URLS.ACCESS_DENIED, { replace: true });
        return; // Stop here, don't do initial redirect logic
      }

      // Step 6: Handle initial navigation after login (runs only once per session)
      // This prevents re-triggering redirects on every route change
      if (!hasHandledInitialRedirect) {
        // Parse URL query parameters to check for redirect instructions
        const searchParams = new URLSearchParams(location.search);
        const redirectUrl = searchParams.get("redirect");
        // Priority 1: Redirect to URL from query param (e.g., /login?redirect=/reports)
        // Used when user was redirected to login from a deep link
        if (redirectUrl) {
          navigate(decodeURIComponent(redirectUrl), { replace: true });

          // Priority 2: Redirect based on backend's redirect_tab hint
          // Backend can suggest where to send user after login
        } else if (redirect_tab) {
          handleRedirectByTab(redirect_tab, navigate);

          // Priority 3: Default redirect when landing on root or login page
          // Automatically route to first accessible page based on user role
        } else if (
          location.pathname === "/" ||
          location.pathname === PAGE_URLS.LOGIN
        ) {
          const firstRoute = getFirstAccessibleRoute(enabled_navigation);
          navigate(firstRoute, { replace: true });
        }

        // Mark that initial redirect has been handled
        // Prevents this logic from running again on subsequent route changes
        setHasHandledInitialRedirect(true);
      }
    }
  }, [
    profileResponse, // Re-run when profile data updates
    dispatch, // Stable reference from Redux
    navigate, // Stable reference from router
    location, // Re-run when route changes (for permission checks)
    hasHandledInitialRedirect, // Re-run if redirect flag changes
  ]);

  // Render logic - NOW all hooks are called before these returns
  if (isChecking || (isAuthenticated && isProfileLoading)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <PageLoader />
      </div>
    );
  }

  if (!isAuthenticated) {
    // ✅ Build full URL with current path and query params
    const currentUrl = `${window.location.origin}${location.pathname}${location.search}`;
    const encodedRedirect = encodeURIComponent(currentUrl);

    return <Navigate to={`/login?redirect=${encodedRedirect}`} replace />;
  }

  return <Outlet />;
};

export default PrivateRoute;
